import {
  differenceInCalendarDays,
  differenceInMinutes,
  format as dateFNSFormat,
  formatDistance as dateFNSFormatDistance,
  formatDistanceStrict as dateFNSFormatDistanceStrict,
  parse as dateFNSParse,
  Locale,
} from 'date-fns';
import { enUS, fr } from 'date-fns/locale';

type FormatDistanceOptionsType = {
  addSuffix?: boolean;
  includeSeconds?: boolean;
  locale?: Locale;
};

type FormatDistanceStrictOptionsType = {
  addSuffix?: boolean;
  locale?: Locale;
  roundingMethod?: 'floor' | 'ceil' | 'round';
  unit?: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';
};

export class DateUtils {
  localeGetter: () => string;

  constructor(localeGetter: () => string) {
    this.localeGetter = localeGetter;
  }

  getDateFormat(locale: string): string {
    switch (locale) {
      case 'fr':
        return 'dd MMM';
      case 'en':
      default:
        return 'MMM do';
    }
  }

  getSlashDateFormat(locale: string): string {
    switch (locale) {
      case 'fr':
        return 'dd/MM/yy';
      case 'en':
      default:
        return 'MM/dd/yy';
    }
  }

  getDaysLeftBeforeDate(dateTime?: number | string | null): number | null {
    if (dateTime !== 0 && !dateTime) {
      return null;
    }
    const date = new Date(dateTime);
    const today = Date.now();
    return Math.max(0, differenceInCalendarDays(date, today));
  }

  getDateTimeLabelDifferenceValue(timestamp: number): string {
    const now = Date.now();
    return differenceInCalendarDays(now, timestamp) > 3
      ? this.format(timestamp, 'PP p')
      : this.formatDistanceLessOneMinute(timestamp);
  }

  format(date: number | Date, dateFormat = 'Pp'): string {
    return dateFNSFormat(date, dateFormat, { locale: this.getDateFNSLocale(this.localeGetter()) });
  }

  parse(date: string, dateFormat: string, backupDate: number | Date = Date.now()): Date {
    return dateFNSParse(date, dateFormat, backupDate, { locale: this.getDateFNSLocale(this.localeGetter()) });
  }

  private getDateFNSLocale(locale: string): Locale {
    switch (locale) {
      case 'fr':
        return fr;
      case 'en':
      default:
        return enUS;
    }
  }

  private formatDistanceLessOneMinute(timestamp: number): string {
    const now = Date.now();
    return differenceInMinutes(now, timestamp) < 1
      ? this.formatDistance(timestamp, now)
      : this.formatDistanceStrict(timestamp, now);
  }

  private formatDistance(
    date: number | Date,
    baseDate: number | Date,
    options: FormatDistanceOptionsType = { addSuffix: true }
  ): string {
    return dateFNSFormatDistance(date, baseDate, {
      ...options,
      locale: this.getDateFNSLocale(this.localeGetter()),
    });
  }

  private formatDistanceStrict(
    date: number | Date,
    baseDate: number | Date,
    options: FormatDistanceStrictOptionsType = { addSuffix: true }
  ): string {
    return dateFNSFormatDistanceStrict(date, baseDate, {
      ...options,
      locale: this.getDateFNSLocale(this.localeGetter()),
    });
  }
}
